import { useEffect, useMemo, useRef } from 'react';
import { useLiveSimulator } from './useLiveSimulator';
import { useReducedMotion } from './useReducedMotion';
import {
  ACTIONABLE_REC_STATUSES, computeHeadline, rankRecs, rankAlerts,
  isFeedDegraded, freshnessAgeSeconds, FRESHNESS_STALE_SECONDS,
} from '../liveModeMeta';

/**
 * Shared Live Mode view-model. Every visual variant (terminal, broadcast, HUD,
 * board) consumes this so they share IDENTICAL data + IA behavior — headline
 * precedence, rec/alert ranking, freshness/degraded logic, reduced-motion — and
 * differ only in how they render. The CEO compares visuals, not logic.
 *
 * Takes the live seed data (from react-query, via LiveModePage) and returns the
 * fully-derived model. No rendering concerns live here.
 */
export function useLiveModeModel(seedAlerts, seedRecs) {
  const reduced = useReducedMotion();
  const animate = !reduced;

  const { alerts, recs, clock, flashId, jitterActive, dataAsOf } = useLiveSimulator(seedAlerts, seedRecs);

  const degraded = useMemo(() => isFeedDegraded(alerts), [alerts]);

  const { criticalCount, watchingCount } = useMemo(() => ({
    criticalCount: alerts.filter((a) => a.status === 'critical').length,
    watchingCount: alerts.filter((a) => a.status === 'warning').length,
  }), [alerts]);

  const actionableRecs = useMemo(
    () => (degraded ? [] : recs.filter((r) => ACTIONABLE_REC_STATUSES.includes(r.status))),
    [recs, degraded],
  );

  const criticalRecIds = useMemo(
    () => new Set(alerts.filter((a) => a.status === 'critical' && a.recId).map((a) => a.recId)),
    [alerts],
  );
  const rankedRecs = useMemo(() => rankRecs(actionableRecs, criticalRecIds), [actionableRecs, criticalRecIds]);
  const rankedAlerts = useMemo(() => rankAlerts(alerts), [alerts]);

  const headline = useMemo(
    () => computeHeadline({ degraded, criticalCount, actionsCount: actionableRecs.length, watchingCount }),
    [degraded, criticalCount, actionableRecs.length, watchingCount],
  );

  const lastValidRef = useRef(null);
  useEffect(() => {
    if (headline.state !== 'degraded') lastValidRef.current = { label: headline.label, at: clock };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headline.state, headline.count]);

  const headlineOut = useMemo(() => {
    if (headline.state === 'degraded' && lastValidRef.current) {
      const t = lastValidRef.current.at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      return { ...headline, subtext: `LAST VALID ${lastValidRef.current.label} @ ${t}` };
    }
    return headline;
  }, [headline]);

  const freshness = useMemo(() => {
    const ageSeconds = freshnessAgeSeconds(dataAsOf, clock);
    return { ageSeconds, stale: degraded || ageSeconds > FRESHNESS_STALE_SECONDS };
  }, [dataAsOf, clock, degraded]);

  return {
    // live data
    alerts, recs, clock, flashId, jitterActive, dataAsOf,
    // derived state
    degraded, criticalCount, watchingCount,
    actionableRecs, rankedRecs, rankedAlerts,
    headline: headlineOut, freshness,
    // presentation flags
    animate,
  };
}
